import { useEffect } from "react"

// Opt-in registry of "there are unsaved edits right now" flags, keyed per form.
// The chunk-error auto-reload (ErrorBoundary / RouteBoundary) checks this before
// reloading: if anything is dirty it shows a MANUAL "Reload" prompt instead of
// yanking the page out from under in-progress work. Default (nothing registered)
// = current behaviour, so this is safe to adopt incrementally.
const dirtyKeys = new Set<string>()

export function setUnsavedWork(key: string, isDirty: boolean): void {
  if (isDirty) dirtyKeys.add(key)
  else dirtyKeys.delete(key)
}

export function hasUnsavedWork(): boolean {
  return dirtyKeys.size > 0
}

/**
 * Register a form's dirty state so the app won't auto-reload over unsaved edits,
 * and warn on accidental tab-close / hard navigation while dirty.
 *
 * Usage: `useUnsavedWork("estimate-editor", isDirty)` inside the editor.
 */
export function useUnsavedWork(key: string, isDirty: boolean): void {
  useEffect(() => {
    setUnsavedWork(key, isDirty)
    if (!isDirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = "" // browsers show their generic "Leave site?" prompt
    }
    window.addEventListener("beforeunload", onBeforeUnload)
    return () => window.removeEventListener("beforeunload", onBeforeUnload)
  }, [key, isDirty])

  // Always clear the flag when the form unmounts.
  useEffect(() => () => setUnsavedWork(key, false), [key])
}
